import { ActionContext } from 'vuex';
import { IPosts } from '@llws/lift-entity-interfaces';

import {
  IApiResponse,
  IFilterPayload,
  IMetaResponse,
  IModifyPayload,
  TPostType,
} from '@/shared';
import posts from '@/shared/services/posts';
import { IPostStore } from '@/shared/interfaces/post-store';
import vm from '@/main';
import { IFilterPayloadV2 } from '../../shared/interfaces/filter-payload-v2';

export default {
  namespaced: true, // 'posts/'

  state: {
    list: undefined,
    single: undefined,
    listByPostType: undefined,
  } as IPostStore,

  getters: {
    list: (state: IPostStore): (IPosts & { publishDateAsString?: string })[] => {
      if (state.list) {
        return (state.list.data || []).map((item) => {
          if (item.status === 'enabled') {
            // eslint-disable-next-line no-param-reassign
            item.status = 'published';
          } else if (item.status === 'disabled') {
            // eslint-disable-next-line no-param-reassign
            item.status = 'private';
          }

          return item;
        });
      }

      return [];
    },

    listByPostType: (state: IPostStore): any => {
      if (state.listByPostType) {
        return state.listByPostType.data;
      }
      return [];
    },

    single: (state: IPostStore): any => {
      if (state.single) {
        let status = state.single?.data?.status;

        if (status === 'enabled') {
          status = 'published';
        } else if (status === 'disabled') {
          status = 'private';
        }

        return {
          ...state.single.data,
          ...{ status },
        };
      }

      return undefined;
    },

    listMeta: (state: IPostStore): any => (state.list && state.list.meta),
  },

  mutations: {
    set: (state: IPostStore, payload: IApiResponse<Array<any>, IMetaResponse>) => {
      state.list = payload;
    },

    setByPostType: (state: IPostStore, payload: IApiResponse<Array<any>, IMetaResponse> | Record<string, any>) => {
      if (Array.isArray(payload.data)) {
        state.listByPostType = payload;

        if (state.single) {
          const cols = Object.keys(state.listByPostType.data![0] || {});
          const idx = state.listByPostType.data!.findIndex((v: any) => v.id === state.single?.data?.id);
          const entry: any = {};

          cols.forEach((k: string) => {
            if (state.single?.data) {
              entry[k] = (state.single?.data as Record<string, any>)[k];
            }
          });

          if (idx !== -1 && !vm()?.$rsGlobals.$isTranslateMode) {
            state.listByPostType.data![idx] = entry;
          } else if (idx === -1 && entry.id) {
            state.listByPostType.data!.unshift(entry);
          }
        }
      } else if ((payload as Record<string, any>).id) {
        state.single = { data: payload as IPosts, meta: null };
      } else if (payload.data) {
        state.single = Array.isArray(payload.data)
          ? { data: payload.data[0] as IPosts, meta: null }
          : payload as IApiResponse<IPosts, any>;
      }
    },

    setPosts(state: any, payload: any) {
      if (Array.isArray(payload.data)) {
        state.listByPostType = payload;
      } else {
        state.single = payload;
      }
    },

    setSingle(state: IPostStore, payload: any) {
      state.single = { data: payload };
    },

    reset(state: IPostStore, resetSingle: boolean = false) {
      state.listByPostType = undefined;
      state.list = undefined;

      if (resetSingle) {
        state.single = undefined;
      }
    },
  },

  actions: {
    get: async (
      ctx: ActionContext<IPostStore, any>,
      payload: IFilterPayloadV2,
    ) => {
      const result = payload.id
        ? await posts.getSingleByType(payload.id, payload.parentId!, payload.additional!.postType as TPostType)
        : await posts.get(payload.parentId!, payload.filters);
      ctx.commit('set', result);
    },

    getByType: async (
      ctx: ActionContext<IPostStore, any>,
      payload: IFilterPayloadV2,
    ) => {
      const result = payload.id
        ? await posts.getSingleByType(payload.id, payload.parentId!, payload.additional!.postType as TPostType)
        : await posts.getByType(payload.parentId!, payload.additional!.postType as TPostType, payload.filters);
      ctx.commit('setByPostType', result);
      return result;
    },

    async removePost(ctx: ActionContext<any, any>, payload: IModifyPayload) {
      await posts.removePost(payload.id!, payload.parentId!, payload.additional!.postType);
    },

    save: async (
      ctx: ActionContext<IPostStore, any>,
      payload: IModifyPayload,
    ) => {
      const { data } = payload as Record<string, any>;
      const siteId = payload.parentId!;
      const postType = payload.additional!.postType as TPostType;
      let newStatus = `${data.status}`;

      if (['Bios', 'Careers', 'FAQs', 'Testimonials'].includes(postType)) {
        if (data.status === 'published') {
          newStatus = 'enabled';
        } else if (data.status === 'private') {
          newStatus = 'disabled';
        }

        data.status = newStatus;
      }

      const result = payload.id
        ? await posts.updateSingleByType(payload.id!, siteId, postType, data)
        : await posts.insertSingleByType(payload.parentId!, postType, data);

      ctx.commit('setByPostType', result);

      if (vm()?.$rsGlobals.$language !== 'en') {
        return data;
      }

      return result.data[1] || result.data[0];
    },

    saveOrder: async (
      ctx: ActionContext<IPostStore, any>,
      payload: IModifyPayload,
    ) => {
      const { data } = payload as Record<string, any>;
      const siteId = payload.parentId!;
      const postType = payload.additional!.postType as TPostType;
      let newStatus = `${data.status}`;

      if (['Bios', 'Careers', 'FAQs', 'Testimonials'].includes(postType)) {
        if (data.status === 'published') {
          newStatus = 'enabled';
        } else if (data.status === 'private') {
          newStatus = 'disabled';
        }

        data.status = newStatus;
      }

      posts.updateByType(siteId, postType, data);
    },
  },
};
